<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <d-col col sm="3" class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">{{ $t('order.orders') }}</span>
        <h3 class="page-title">
          <template v-if="isViewMode">{{ $t('order.orderPreview') }}</template>
          <template v-else-if="isDraftMode && !isEditMode">{{ $t('order.addNewDraft') }}</template>
          <template v-else-if="isDraftMode && isEditMode">{{ $t('order.editDraft') }}</template>
          <template v-else>{{ $t('order.addNewOrder') }}</template>
        </h3>
      </d-col>

      <d-col col sm="9" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="secondary"
          class="mr-2"
          @click.prevent="backUrl()"
          :disabled="submitInProgress">
          <i class="material-icons">arrow_back</i>
          {{ $t('ui.backToList') }}
        </d-button>

        <d-button
          v-if="isViewMode || isEditMode"
          theme="warning"
          @click.prevent="downloadPDFforMe"
          class="ml-2"
          :disabled="downloadInProgress">
          <i class="material-icons">cloud_download</i>
          {{ $t('order.downloadPDFforMe') }}
        </d-button>

        <d-button
          v-if="isViewMode || isEditMode"
          theme="info"
          @click.prevent="downloadPDF"
          class="ml-2"
          :disabled="downloadInProgress">
          <i class="material-icons">cloud_download</i>
          {{ $t('order.downloadPDFforMyCustomer') }}
        </d-button>

        <d-button
          v-if="!isViewMode"
          theme="primary"
          @click.prevent="beforeAddDraft"
          class="ml-2"
          :disabled="submitInProgress || shippingPriceUpdateInProgress">
          <i class="material-icons">save</i>
          {{ $t('order.saveDraft') }}
        </d-button>

        <d-button
          v-if="!isViewMode"
          theme="success"
          @click.prevent="beforeAddOrder"
          class="ml-2"
          :disabled="submitInProgress || shippingPriceUpdateInProgress">
          <i class="material-icons">save</i>
          {{ $t('order.saveAsOrder') }}
        </d-button>
      </d-col>
    </d-row>

    <!-- Form -->
    <d-form>
      <d-alert
        v-if="whoOrders === 'me' && !isViewMode"
        theme="warning"
        show>
        <span v-pure-html="$t('order.editingOfferFromHPL1')"></span>
        <a href="#" @click.prevent="whoOrders = 'my-customer'">{{ $t('order.editingOfferFromHPL2') }}</a>.
        <br>
        <strong>{{ $t('order.editingOfferFromHPL3') }}</strong>
      </d-alert>

      <validation-observer ref="request-form" tag="form" @keydown.enter.prevent>
        <d-card class="mb-5">
          <d-tabs
            v-if="!loadError"
            card
            :nav-class="allDataLoaded ? validationResults : ''">

            <!-- Dane ogólne -->
            <d-tab
              :title="$t('ui.generalInfo')"
              active>
              <h4 class="mb-3 pb-3 border-bottom d-flex">
                {{ $t('ui.generalInfo') }}
              </h4>

              <d-row class="mb-2">
                <d-col cols="12">
                  <label>
                    {{ $t('ui.orderName') }}
                  </label>

                  <validation-provider
                    :rules="isEditMode ? 'required|value_contains:' + requestData.id : 'required'"
                    name="order_name"
                    v-slot="{ errors }">
                    <d-input
                      :state="errors.length ? 'invalid' : null"
                      v-model="order_name"
                      @input="debouncedOrderNameCheck()"
                      :disabled="isViewMode"
                      type="text" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                    <small
                      v-if="orderNameCheckInProgress"
                      class="text-gray mt-2">
                      <div class="spinner-border spinner-border-sm" role="status"></div>
                      {{ $t('order.nameCheck.inProgress') }}
                    </small>
                    <small
                      v-if="order_name && !orderNameCheckInProgress && !orderNameIsFree && orderNameCheckDone"
                      class="text-danger mt-2">
                      <i class="material-icons">block</i>
                      {{ $t('order.nameCheck.inUse') }}
                    </small>
                    <small
                      v-if="order_name && !orderNameCheckInProgress && orderNameIsFree && orderNameCheckDone"
                      class="text-success mt-2">
                      <i class="material-icons">check</i>
                      {{ $t('order.nameCheck.isAvailable') }}
                    </small>
                  </validation-provider>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="6">
                  <label v-if="isViewMode">
                    {{ $t('order.orderPreparedFor') }}:
                  </label>
                  <label v-else>
                    {{ $t('order.offerPreparedFor') }}:
                  </label>
                  <d-button-group
                    v-if="!isViewMode"
                    size="small"
                    class="ml-2">
                    <d-button
                      theme="primary"
                      :outline="whoOrders !== 'me'"
                      @click.prevent="whoOrders = 'me'">
                      {{ $t('order.whoOrdersMe') }}
                    </d-button>
                    <d-button
                      theme="primary"
                      :outline="whoOrders !== 'my-customer'"
                      @click.prevent="whoOrders = 'my-customer'">
                      {{ $t('order.whoOrdersMyCustomer') }}
                    </d-button>
                  </d-button-group>
                  <span
                    v-else
                    class="text-primary">
                    <template v-if="whoOrders === 'me'">
                      {{ $t('order.whoOrdersMe') }}
                    </template>
                    <template v-if="whoOrders === 'my-customer'">
                      {{ $t('order.whoOrdersMyCustomer') }}
                    </template>
                  </span>
                </d-col>

                <d-col
                  v-if="whoOrders === 'my-customer'"
                  cols="6">
                  <d-checkbox
                    v-if="!isViewMode"
                    inline
                    v-model="use_customer_pricing"
                    toggle
                    toggle-small
                    class="mt-2">
                    {{ $t('order.usePartnerPricing') }}
                  </d-checkbox>
                  <template v-else>
                    {{ $t('order.usePartnerPricing') }}:
                    <span v-if="partner_include_discounts" class="text-success">{{ $t('ui.yes') }}</span>
                    <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                  </template>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col
                  v-if="isMainUser"
                  cols="6">
                  <template v-if="isViewMode">
                    <label>{{ $t('ui.leadingPerson') }}:</label>
                    <div class="info-data mb-2">
                      {{ requestData.contact_name }}<br>
                      <template v-if="requestData.contact_email">
                        {{ $t('ui.eMailShort') }}: {{ requestData.contact_email }}<br>
                      </template>
                      <template v-if="requestData.contact_phone">
                        {{ $t('ui.phoneShort') }}: {{ requestData.contact_phone }}
                      </template>
                    </div>
                  </template>

                  <order-select-contact
                    v-if="!isViewMode && isMainUser && allDataLoaded"
                    ref="request-contact"
                    :customer-id="customerID"
                    :required="false"
                    :isMainContact="true" />
                </d-col>

                <d-col cols="6">
                  <template v-if="customer">
                    <h5>{{ $t('order.yourDiscounts') }}:</h5>
                    {{ $t('customer.discount.productDiscount') }}: <span class="text-primary">{{ customer.discount_products }}%</span><br>
                    {{ $t('customer.discount.servicesDiscount') }}: <span class="text-primary">{{ customer.discount_services }}%</span><br>
                    {{ $t('customer.discount.deliveryDiscount') }}: <span class="text-primary">{{ customer.discount_shipping }}%</span>
                  </template>
                </d-col>
              </d-row>

              <hr>

              <d-row
                v-if="whoOrders === 'my-customer'"
                class="mt-3">
                <d-col cols="6">
                  <label>{{ $t('users.customer') }}:</label>

                  <div class="d-flex">
                    <select-customer
                      v-if="!isViewMode"
                      ref="request-customer"
                      :required="true"
                      :customerID="partner_customer_id" />

                    <d-button
                      v-if="!isViewMode && userIsCustomer"
                      theme="primary"
                      outline
                      class="ml-4 text-nowrap w-50"
                      @click.prevent="$bus.$emit('order-show-new-customer-popup')">
                      <i class="material-icons">add</i>
                      {{ $t('customer.addNewCustomer') }}
                    </d-button>
                  </div>

                  <span
                    v-if="isViewMode"
                    class="text-primary">
                    {{ requestData.partner_customer_name }}
                  </span>
                </d-col>

                <d-col cols="6">
                  <d-checkbox
                    v-if="!isViewMode"
                    inline
                    v-model="partner_include_discounts"
                    toggle
                    toggle-small
                    class="mt-4">
                    {{ $t('order.useCustomerDiscounts') }}
                  </d-checkbox>
                  <template v-else>
                    {{ $t('order.useCustomerDiscounts') }}:
                    <span v-if="partner_include_discounts" class="text-success">{{ $t('ui.yes') }}</span>
                    <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                  </template>
                </d-col>
              </d-row>

              <d-row v-if="whoOrders === 'my-customer' && partner_include_discounts" class="mt-3">
                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountProducts') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_products"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partner_discount_products"
                          type="text"
                          class="text-right form-control"
                          :disabled="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <small
                      v-if="!isViewMode && parseFloat(current_partner_discount_products) !== parseFloat(partner_discount_products)"
                      class="text-primary">
                      {{ $t('order.currentDiscountOfCustomer') }}: {{ current_partner_discount_products }}%
                    </small>
                  </div>
                </d-col>

                <hr v-if="whoOrders === 'my-customer'">

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partner_discount_services"
                          type="text"
                          class="text-right form-control"
                          :disabled="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <small
                      v-if="!isViewMode && parseFloat(current_partner_discount_services) !== parseFloat(partner_discount_services)"
                      class="text-primary">
                      {{ $t('order.currentDiscountOfCustomer') }}: {{ current_partner_discount_services }}%
                    </small>
                  </div>
                </d-col>

                <d-col cols="4">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.partnerDiscountShipping') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|max_value:100|only-numbers"
                      name="partner_discount_shipping"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <input
                          v-model="partner_discount_shipping"
                          type="text"
                          class="text-right form-control"
                          :disabled="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>

                    <small
                      v-if="!isViewMode && parseFloat(current_partner_discount_shipping) !== parseFloat(partner_discount_shipping)"
                      class="text-primary">
                      {{ $t('order.currentDiscountOfCustomer') }}: {{ current_partner_discount_shipping }}%
                    </small>
                  </div>
                </d-col>
              </d-row>

              <hr v-if="whoOrders === 'my-customer'">

              <d-row
                v-if="whoOrders === 'my-customer'"
                class="mt-3">
                <d-col cols="6">
                  <order-select-contact
                    v-if="!isViewMode"
                    ref="request-customer-contact"
                    mode="partner-customer"
                    :required="false"
                    :customer-id="partner_customer_id" />

                  <template v-if="isViewMode">
                    <label>
                      {{ $t('order.clientContactPerson') }}:
                    </label>
                    <div class="info-data mb-2">
                      {{ requestData.partner_contact_name }}<br>
                      <template v-if="requestData.partner_contact_email">
                        {{ $t('ui.eMailShort') }}: {{ requestData.partner_contact_email }}<br>
                      </template>
                      <template v-if="requestData.partner_contact_phone">
                        {{ $t('ui.phoneShort') }}: {{ requestData.partner_contact_phone }}
                      </template>
                    </div>
                  </template>
                </d-col>
              </d-row>

              <d-row class="mt-3">
                <d-col cols="6">
                  <order-select-address
                    v-if="!isViewMode"
                    :hidden="isEditMode && !addressHasBeenChanged"
                    :isHiddenInUi="isEditMode && !addressHasBeenChanged"
                    ref="request-address"
                    :mode="whoOrders === 'me' ? 'customer' : 'partner-customer'"
                    :customer-id="whoOrders === 'me' ? customerID : partner_customer_id" />

                  <template v-if="isViewMode || (isEditMode && !addressHasBeenChanged)">
                    <label>
                      {{ $t('order.shippingAddress') }}:
                    </label>
                    <div class="info-data mb-2">
                      {{ requestData.address_company_name }}<br>

                      <template v-if="requestData.address_name">
                      {{ requestData.address_name }}<br>
                      </template>

                      <template v-if="requestData.address_street">
                      {{ requestData.address_street }} {{ requestData.address_building }} <template v-if="requestData.address_locale">/ {{ requestData.address_locale }}</template><br>
                      </template>

                      <template v-if="requestData.address_city || requestData.address_postcode">
                      {{ requestData.address_city }} {{ requestData.address_postcode }}<br>
                      </template>

                      <template v-if="requestData.address_country">
                      {{ requestData.address_country }}<br>
                      </template>

                      <template v-if="requestData.address_email">
                        {{ $t('ui.eMailShort') }}: {{ requestData.address_email }}<br>
                      </template>

                      <template v-if="requestData.address_phone">
                        {{ $t('ui.phoneShort') }}: {{ requestData.address_phone }}
                      </template>

                      <small
                        v-if="isEditMode"
                        class="text-primary cursor-pointer mt-2 d-block"
                        @click.prevent="addressHasBeenChanged = true;">
                        <i class="material-icons">edit</i>
                        {{ $t('ui.edit') }}
                      </small>
                    </div>
                  </template>
                </d-col>
              </d-row>

              <d-row v-if="!isEditMode">
                <d-col cols="6">
                  <label>
                    {{ $t('inquiries.trackingNumber') }}:
                  </label>

                  <p>
                    <span v-if="!requestData || !requestData.tracking_number">
                      {{ $t('ui.lack') }}
                    </span>
                    <span
                      v-else-if="requestData && requestData.tracking_number"
                      class="text-primary">
                      {{ requestData.tracking_number }}
                    </span>
                  </p>
                </d-col>
              </d-row>

              <d-row>
                <d-col cols="3">
                  <label>
                    {{ $t('inquiries.needExecutionTill') }}:
                  </label>

                  <d-datepicker
                    v-if="!isViewMode"
                    v-model="realization_date"
                    :monday-first="true"
                    format="yyyy-MM-dd"
                    :placeholder="$t('ui.chooseDate')"
                    :language="translationsForDatePicker" />

                  <p
                    v-if="isViewMode"
                    class="text-primary">
                    {{ requestRealizationDate }}
                  </p>
                </d-col>

                <d-col cols="3">
                  <label for="processing-time">
                    {{ $t('inquiries.executionTime') }}:
                  </label>

                  <div
                    v-if="isViewMode"
                    class="text-primary">
                    <template v-if="requestData.processing_time_name">
                      {{ requestData.processing_time_name }}
                    </template>
                    <template v-else>
                      &mdash;
                    </template>
                  </div>

                  <div
                    v-else
                    :class="{
                      'form-control': true,
                      'is-select': true
                    }">
                    <v-select
                      :options="processingTimeOptions"
                      v-model="processing_time_id"
                      :searchable="false"
                      :clearable="false"
                      :reduce="processingTime => processingTime.value"
                      :placeholder="$t('inquiries.selectExecutionTime')"
                      label="text">
                    </v-select>
                  </div>
                </d-col>
              </d-row>

              <hr>

              <d-row>
                <d-col cols="3">
                  <order-select-lang
                    v-if="!isViewMode"
                    :initialLanguageID="language_id"
                    ref="order-lang" />
                  <template v-if="isViewMode">
                    <label>
                      {{ $t('order.offerLanguage') }}:
                    </label>
                    <span class="text-primary">
                      {{ requestData.language_code }}
                    </span>
                  </template>
                </d-col>

                <d-col cols="3">
                  <order-select-currency
                    v-if="!isViewMode && allDataLoaded"
                    :initialCurrencyID="currency_id"
                    ref="order-currency" />
                  <template v-if="isViewMode">
                    <label>
                      {{ $t('order.offerCurrency') }}:
                    </label>
                    <span class="text-primary">
                      {{ requestData.currency_code }}
                    </span>
                  </template>
                </d-col>

                <d-col cols="3">
                  <d-checkbox
                    v-if="!isViewMode"
                    inline
                    v-model="offer_for_each_product"
                    toggle
                    toggle-small
                    class="mt-4">
                    {{ $t('product.breakProductsIntoSeparateOffers') }}
                  </d-checkbox>
                  <template v-else>
                    {{ $t('product.breakProductsIntoSeparateOffers') }}:
                    <span v-if="offer_for_each_product" class="text-success">{{ $t('ui.yes') }}</span>
                    <span v-else class="text-danger">{{ $t('ui.no') }}</span>
                  </template>
                </d-col>
              </d-row>

              <hr v-if="whoOrders === 'my-customer'">

              <d-row v-if="whoOrders === 'my-customer'">
                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupProducts') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_products"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_products"
                          type="text"
                          class="text-right"
                          :readOnly="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_services"
                          type="text"
                          class="text-right"
                          :readOnly="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupSetupServices') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_setup_services"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_setup_services"
                          type="text"
                          class="text-right"
                          :readOnly="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>

                <d-col cols="2">
                  <div class="form-group">
                    <label>
                      {{ $t('ui.markupShipping') }}:
                    </label>

                    <validation-provider
                      rules="required|min_value:0|only-numbers"
                      name="markup_shipping"
                      v-slot="{ errors }">
                      <d-input-group
                        append="%"
                        :class="{ 'is-invalid': errors && errors.length }">
                        <d-input
                          v-model="markup_shipping"
                          type="text"
                          class="text-right"
                          :readOnly="isViewMode"
                          :state="errors.length ? 'invalid' : null" />
                      </d-input-group>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

              <hr>

              <div class="form-group">
                <label for="description">
                  {{ $t('inquiries.orderAdditionalInfo') }}:
                </label>

                <d-form-textarea
                  v-if="!isViewMode"
                  v-model="customer_description"
                  :value.prop="customer_description"
                  :placeholder="$t('inquiries.addComments')"
                  :rows="6" />

                <pre
                  v-if="isViewMode"
                  class="customer-desc">
                  <template v-if="requestData.customer_description">{{ requestData.customer_description }}</template><template v-else>{{ $t('inquiries.noComments') }}&hellip;</template>
                </pre>
              </div>

              <order-files
                ref="customer-request-files"
                :orderID="requestData ? requestData.id : 0"
                :orderFiles="requestData ? requestData.files : []"
                :readOnly="isViewMode" />
            </d-tab>

            <!-- Zapytanie -->
            <d-tab :title="$t('ui.order')">
              <request-creator
                v-if="allDataLoaded"
                ref="request-creator"
                :readOnly="isViewMode"
                :products="products"
                :currencyID="currency_id"
                :currencies="currencies"
                :discountProducts="parseFloat(customer.discount_products)"
                :discountServices="parseFloat(customer.discount_services)"
                :discountShipping="parseFloat(customer.discount_shipping)"
                :partnerDiscountProducts="parseFloat(partner_discount_products)"
                :partnerDiscountServices="parseFloat(partner_discount_services)"
                :partnerDiscountShipping="parseFloat(partner_discount_shipping)"
                :embroideryItems="embroideryItems"
                :markupProducts="parseFloat(markup_products)"
                :markupServices="parseFloat(markup_services)"
                :markupSetupServices="parseFloat(markup_setup_services)"
                :markupShipping="parseFloat(markup_shipping)"
                :services="services"
                :servicesGroups="servicesGroups"
                :whoOrders="whoOrders"
                :useCustomerPricing="use_customer_pricing"
                :tagCategories="tagCategories"
                :orderSetUpInProgress="orderSetUpInProgress"
                :cartData="cartData"
                :shipperPrice="shipper_price"
                :shipperPriceBeforeDiscount="shipper_price_before_discount"
                :partnerShipperPrice="partner_shipper_price"
                :partnerShipperPriceBeforeDiscount="partner_shipper_price_before_discount"
                :boxesAlert="boxesAlert"
                :boxesCount="boxesCount"
                :productsCount="productsCount" />
            </d-tab>

            <!-- Powiązane faktury -->
            <d-tab
              v-if="isViewMode"
              :title="$t('order.connectedInvoices')">
              <order-settlements
                :settlements="requestData.settlements"
                :orderName="requestData.order_name" />
            </d-tab>
          </d-tabs>

          <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
            {{ $t('ui.dataLoading') }}&hellip;
          </div>

          <d-alert
            v-if="loadError"
            show
            theme="warning">
            {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
          </d-alert>
        </d-card>
      </validation-observer>
    </d-form>

    <order-add-customer-popup
      v-if="showAddCustomerPopup" />

    <customer-contact-person-new-popup
      v-if="showContactPersonAddPopup"
      :mode="contactPersonPopupMode"
      :customerID="contactPersonPopupMode === 'partner' ? customerID : partner_customer_id" />

    <customer-address-new-popup
      v-if="showAddressAddPopup"
      :mode="whoOrders === 'me' ? 'partner' : 'partner-customer'"
      :countries="countries"
      :customerID="whoOrders === 'me' ? customerID : partner_customer_id" />

    <container-search-popup
      v-if="showContainerSearchPopup"
      :productID="selectedContainerProduct"
      :orderProductID="selectedContainerOrderProduct"
      :missingAmount="missingProductAmount" />

    <d-modal
      v-if="showPriceChangesPopup"
      @close="showPriceChangesPopup = false"
      size="lg">
      <d-modal-header>
        <d-modal-title class="px-0">
          {{ $t('order.priceChangePopup.title') }}<br>
          <small>
            {{ $t('order.priceChangePopup.subtitle') }}
          </small>
        </d-modal-title>
      </d-modal-header>
      <d-modal-body class="pt-4 px-4">
        <table class="table">
          <thead>
            <th>
              {{ $t('order.priceChangePopup.type') }}
            </th>
            <th>
              {{ $t('order.priceChangePopup.element') }}
            </th>
            <th>
              {{ $t('order.priceChangePopup.previous') }}
            </th>
            <th>
              {{ $t('order.priceChangePopup.new') }}
            </th>
          </thead>
          <tbody>
            <tr
              v-for="(priceChange, index) of priceChanges"
              :key="'price-change-item-' + index">
              <td>
                <template v-if="priceChange.type === 'product'">
                  {{ $t('order.priceChangePopup.types.product') }}
                </template>
                <template v-else-if="priceChange.type === 'global-service-multiple-from'">
                  {{ $t('order.priceChangePopup.types.globalServiceMultipleFrom') }}
                </template>
                <template v-else-if="priceChange.type === 'global-service-setup-price'">
                  {{ $t('order.priceChangePopup.types.globalServiceSetupPrice') }}
                </template>
                <template v-else-if="priceChange.type === 'global-service-price'">
                  {{ $t('order.priceChangePopup.types.globalServicePrice') }}
                </template>
                <template v-else-if="priceChange.type === 'service-multiple-from'">
                  {{ $t('order.priceChangePopup.types.serviceMultipleFrom') }}
                </template>
                <template v-else-if="priceChange.type === 'service-setup-price'">
                  {{ $t('order.priceChangePopup.types.serviceSetupPrice') }}
                </template>
                <template v-else-if="priceChange.type === 'service-price'">
                  {{ $t('order.priceChangePopup.types.servicePrice') }}
                </template>
                <br>
                <small v-if="priceChange.source">
                  <strong>
                    {{ $t('order.priceChangePopup.changeSource') }}:
                  </strong>
                  <span class="text-primary">
                    {{ $t('order.priceChangePopup.sources.' + priceChange.source) }}
                  </span>
                </small>
              </td>
              <td>
                {{ priceChange.name }} ({{ priceChange.code }})
              </td>
              <td class="text-danger">
                <template v-if="priceChange.type === 'global-service-multiple-from' || priceChange.type === 'service-multiple-from'">
                  {{ priceChange.old_price_multiple_from }}
                </template>
                <template v-else-if="priceChange.type === 'global-service-setup-price' || priceChange.type === 'service-setup-price'">
                  {{ priceChange.old_setup_price }} {{ selectedCurrencySymbol }}
                </template>
                <template v-else>
                  {{ priceChange.old_price }} {{ selectedCurrencySymbol }}
                </template>
              </td>
              <td class="text-success">
                <template v-if="priceChange.type === 'global-service-multiple-from' || priceChange.type === 'service-multiple-from'">
                  {{ priceChange.new_price_multiple_from }}
                </template>
                <template v-else-if="priceChange.type === 'global-service-setup-price' || priceChange.type === 'service-setup-price'">
                  {{ priceChange.new_setup_price }} {{ selectedCurrencySymbol }}
                </template>
                <template v-else>
                  {{ priceChange.new_price }} {{ selectedCurrencySymbol }}
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </d-modal-body>
    </d-modal>
  </d-container>
</template>

<script>
import ContainerSearchPopup from './../orders/ContainerSearchPopup.vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import CustomerContactPersonNewPopup from './../customers/CustomerContactPersonNewPopup.vue';
import CustomerAddressNewPopup from './../customers/CustomerAddressNewPopup.vue';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import datepickerTranslations from './../../data/datepicker-translations.js';
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import OrderAddCustomerPopup from './OrderAddCustomerPopup.vue';
import OrderFiles from './OrderFiles.vue';
import OrderSelectAddress from './OrderSelectAddress.vue';
import OrderSelectContact from './OrderSelectContact.vue';
import OrderSelectCurrency from './OrderSelectCurrency.vue';
import OrderSelectLang from './OrderSelectLang.vue';
import OrderSettlements from './OrderSettlements.vue';
import SelectCustomer from './../common/SelectCustomer.vue';
import RequestCreator from './RequestCreator.vue';
import Vue from 'vue';
import getPrefferedLanguage from './../../utils/i18nUtils.js';

export default {
  name: 'request-customer-view',
  props: [
    'requestData',
    'cartData'
  ],
  mixins: [
    CustomerPanel
  ],
  components: {
    'container-search-popup': ContainerSearchPopup,
    'customer-contact-person-new-popup': CustomerContactPersonNewPopup,
    'customer-address-new-popup': CustomerAddressNewPopup,
    'order-add-customer-popup': OrderAddCustomerPopup,
    'order-files': OrderFiles,
    'order-select-address': OrderSelectAddress,
    'order-select-contact': OrderSelectContact,
    'order-select-lang': OrderSelectLang,
    'order-select-currency': OrderSelectCurrency,
    'order-settlements': OrderSettlements,
    'select-customer': SelectCustomer,
    'request-creator': RequestCreator
  },
  computed: {
    customerID () {
      return parseInt(this.$store.getters.getCustomerID, 10);
    },
    isViewMode () {
      return !!this.$route.params.id && this.$route.path.indexOf('/zamowienia/podglad/') > -1;
    },
    isDraftMode () {
      return this.$route.path.indexOf('/oferty') > -1;
    },
    isEditMode () {
      return !!this.$route.params.id && this.$route.path.indexOf('/oferty/edycja/') > -1;
    },
    isMainUser () {
      return this.$store.getters.getParentID === 0;
    },
    allDataLoaded () {
      return this.loadedData.join(',') === 'countries,currencies,customer,embroidery-items,processing-times,products,services,services-groups';
    },
    validationResults () {
      let errors = this.$refs['request-form'].errors;
      let allKeys = Object.keys(errors);
      let errorKeys = [];
      let errorTabs = [];
      let results = [
        ['error-1', ['partner_contact_id', 'partner_customer_id', 'markup_products', 'markup_services', 'markup_setup_services', 'markup_shipping', 'contact_id', 'address_id']],
        ['error-2', ['product-id-', 'product-', 'product-amount-', 'service-', 'global-service-']]
      ];

      for (let i = 0; i < allKeys.length; i++) {
        if (errors[allKeys[i]].length) {
          errorKeys.push(allKeys[i]);
        }
      }

      errorKeys = errorKeys.join(',');

      for (let i = 0; i < results.length; i++) {
        for (let j = 0; j < results[i][1].length; j++) {
          let key = results[i][1][j];

          if (errorKeys.indexOf(key) > -1) {
            errorTabs.push(results[i][0]);
            break;
          }
        }
      }

      if (!this.orderNameIsFree) {
        errorTabs.push('error-1');
      }

      return errorTabs.join(' ');
    },
    translationsForDatePicker () {
      return datepickerTranslations[getPrefferedLanguage()];
    },
    requestRealizationDate () {
      if (this.requestData.realization_date) {
        return [
          this.requestData.realization_date.getFullYear(),
          this.requestData.realization_date.getMonth() + 1,
          this.requestData.realization_date.getDate()
        ].join('-');
      }

      return this.$t('inquiries.noDateSelected');
    },
    selectedCurrencySymbol () {
      let foundedCurrency = this.currencies.filter(currency => currency.id === this.currency_id);

      if (foundedCurrency.length) {
        return foundedCurrency[0].code;
      }

      return '';
    },
    processingTimeOptions () {
      return this.processingTimes.map(processingTime => ({
        value: processingTime.id,
        text: processingTime.name
      }));
    },
    hasPriceMultipleFromChanges () {
      if (this.priceChanges && this.priceChanges.length) {
        for (let i = 0; i < this.priceChanges.length; i++) {
          if (this.priceChanges[i].type === 'global-service-multiple-from' || this.priceChanges[i].type === 'service-multiple-from') {
            return true;
          }
        }
      }

      return false;
    }
  },
  watch: {
    whoOrders (newValue) {
      if (!this.orderSetUpInProgress) {
        if (newValue === 'me') {
          this.markup_products = 0;
          this.markup_services = 0;
          this.markup_setup_services = 0;
          this.markup_shipping = 0;
          this.use_customer_pricing = false;
          this.updateOfferLanguage(this.customer.language_id);
          this.partner_customer_id = null;
          this.selectedCustomer = null;
          this.selectedAddressData = null;
          this.selectedPersonData = null;
        }

        if (newValue === 'my-customer' && !this.isViewMode) {
          this.updateOfferLanguage(this.customer.language_id);
          this.markup_products = this.customer.markup_products;
          this.markup_services = this.customer.markup_services;
          this.markup_setup_services = this.customer.markup_setup_services;
          this.markup_shipping = this.customer.markup_shipping;
        }

        Vue.nextTick(() => {
          this.$bus.$emit('request-creator-global-product-update-price');
          this.$bus.$emit('request-creator-global-services-update-price');
        });
      }
    },
    markup_products () {
      if (!this.orderSetUpInProgress) {
        Vue.nextTick(() => {
          this.$bus.$emit('request-creator-global-product-update-price');
        });
      }
    },
    markup_services () {
      if (!this.orderSetUpInProgress) {
        Vue.nextTick(() => {
          this.$bus.$emit('request-creator-global-services-update-price');
        });
      }
    },
    markup_setup_services () {
      if (!this.orderSetUpInProgress) {
        Vue.nextTick(() => {
          this.$bus.$emit('request-creator-global-services-update-price');
        });
      }
    },
    markup_shipping () {
      if (!this.orderSetUpInProgress) {
        Vue.nextTick(() => {
          this.$bus.$emit('order-update-shipment-cost');
        });
      }
    },
    loadedCountries () {
      this.$bus.$emit('request-data-fetch-success', 'countries');
    },
    loadedCurrencies () {
      this.$bus.$emit('request-data-fetch-success', 'currencies');
    },
    loadedProducts () {
      this.$bus.$emit('request-data-fetch-success', 'products');
    },
    loadedServices () {
      this.$bus.$emit('request-data-fetch-success', 'services');
    },
    loadedServicesGroups () {
      this.$bus.$emit('request-data-fetch-success', 'services-groups');
    },
    loadedCustomer () {
      this.$bus.$emit('request-data-fetch-success', 'customer');
    },
    loadedEmbroideryItems () {
      this.$bus.$emit('request-data-fetch-success', 'embroidery-items');
    },
    loadedProcessingTimes () {
      this.$bus.$emit('request-data-fetch-success', 'processing-times');
    },
    allDataLoaded (newValue) {
      if (newValue && (this.isEditMode || this.isViewMode)) {
        this.setUpOrder();
      }
    },
    partner_include_discounts (newValue) {
      if (this.orderSetUpInProgress || this.isViewMode) {
        return;
      }

      if (newValue) {
        this.current_partner_discount_products = this.$refs['request-customer'].discounts.products;
        this.current_partner_discount_services = this.$refs['request-customer'].discounts.services;
        this.current_partner_discount_shipping = this.$refs['request-customer'].discounts.shipping;
        this.partner_discount_products = this.$refs['request-customer'].discounts.products;
        this.partner_discount_services = this.$refs['request-customer'].discounts.services;
        this.partner_discount_shipping = this.$refs['request-customer'].discounts.shipping;
      } else {
        this.partner_discount_products = 0;
        this.partner_discount_services = 0;
        this.partner_discount_shipping = 0;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('request-creator-global-product-update-price');
        this.$bus.$emit('request-creator-global-services-update-price');
      });
    },
    partner_discount_products () {
      if (this.orderSetUpInProgress) {
        return;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('request-creator-global-product-update-price');
      });
    },
    partner_discount_services () {
      if (this.orderSetUpInProgress) {
        return;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('request-creator-global-product-update-price');
        this.$bus.$emit('request-creator-global-services-update-price');
      });
    },
    partner_discount_shipping () {
      if (this.orderSetUpInProgress) {
        return;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('order-check-shipping-price');
      });
    },
    use_customer_pricing () {
      if (this.orderSetUpInProgress) {
        return;
      }

      Vue.nextTick(() => {
        this.$bus.$emit('request-creator-global-product-update-price');
        this.$bus.$emit('request-creator-global-services-update-price');
      });
    }
  },
  data() {
    return {
      realization_date: null,
      processing_time_id: null,
      customer_description: '',
      customer: null,
      products: [],
      services: [],
      servicesGroups: [],
      partner_customer_id: null,
      shipper_id: null,
      markup_products: 0,
      markup_services: 0,
      markup_setup_services: 0,
      markup_shipping: 0,
      order_name: '',
      shipper_price: 0,
      shipper_price_before_discount: 0,
      partner_shipper_price: 0,
      partner_shipper_price_before_discount: 0,
      currency_id: null,
      language_id: null,
      offer_for_each_product: false,
      partner_include_discounts: false,
      partner_discount_products: 0,
      partner_discount_services: 0,
      partner_discount_shipping: 0,
      use_customer_pricing: false,
      // data dynamically loaded
      countries: [],
      currencies: [],
      embroideryItems: [],
      processingTimes: [],
      // temporary data
      whoOrders: 'me',
      selectedCustomer: null,
      selectedAddressData: null,
      selectedPersonData: null,
      selectedContainerProduct: null,
      selectedContainerOrderProduct: null,
      missingProductAmount: 0,
      showContainerSearchPopup: false,
      showAddCustomerPopup: false,
      boxesCount: 0,
      boxesAlert: false,
      productsCount: 0,
      lastCheckedOrderName: '',
      showPriceChangesPopup: false,
      priceChanges: [],
      current_partner_discount_products: 0,
      current_partner_discount_services: 0,
      current_partner_discount_shipping: 0,
      // ui flags
      showContactPersonAddPopup: false,
      showAddressAddPopup: false,
      // flags
      loadError: false,
      loadedData: [],
      loadedCountries: false,
      loadedCurrencies: false,
      loadedCustomer: false,
      loadedProducts: false,
      loadedProcessingTimes: false,
      loadedServices: false,
      loadedServicesGroups: false,
      loadedEmbroideryItems: false,
      downloadInProgress: false,
      orderSetUpInProgress: true,
      submitInProgress: false,
      contactPersonPopupMode: false,
      shippingPriceUpdateInProgress: false,
      errorDuringLoadingShippingPrice: false,
      skippedFirstOrderNameCheck: false,
      orderNameCheckInProgress: false,
      orderNameIsFree: true,
      orderNameCheckDone: false,
      addressHasBeenChanged: false
    };
  },
  mounted () {
    this.orderSetUpInProgress = true;

    if (!this.isEditMode && !this.isViewMode) {
      this.orderSetUpInProgress = false;
    }

    this.loadAdditionalData();
    this.$bus.$on('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$on('ui-select-customer-prefetched-discounts', this.updateCurrentDiscounts);
    this.$bus.$on('request-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$on('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$on('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$on('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$on('order-show-add-address-popup', this.showAddressNewPopup);
    this.$bus.$on('container-search-popup-close', this.closeContainerSearchPopup);
    this.$bus.$on('container-search-popup-open', this.openContainerSearchPopup);
    this.$bus.$on('order-check-shipping-price', this.debouncedShippingPriceCheck);
    this.$bus.$on('order-update-shipment-cost', this.debouncedShippingPriceCheck);
    this.$bus.$on('ui-select-customer-language-updated', this.updateOfferLanguage);
    this.$bus.$on('request-creator-show-price-changes-popup', this.displayPriceChanges);
    this.$bus.$on('order-currency-change', this.changeOrderCurrency);
    this.$bus.$on('order-data-language-id-update', this.updateLanguage);
    this.$bus.$on('order-show-new-customer-popup', this.openAddCustomerPopup);
    this.$bus.$on('order-close-new-customer-popup', this.closeAddCustomerPopup);
    this.$bus.$on('order-customer-added-from-popup', this.setCustomerInSelectField);
  },
  methods: {
    backUrl () {
      if (this.isDraftMode) {
        this.$router.push('/oferty/');
        return;
      }

      this.$router.push('/zamowienia');
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/customers/item',
        loadedKey: 'loadedCustomer',
        outputKey: 'customer',
        errorKey: 'loadError',
        successAction: () => {
          this.shipper_id = this.customer.shipper_id;

          if (!this.isEditMode && !this.isViewMode) {
            this.currency_id = this.customer.currency_id;
          }

          if (!this.isEditMode && !this.isViewMode) {
            this.markup_products = this.customer.markup_products;
            this.markup_services = this.customer.markup_services;
            this.markup_setup_services = this.customer.markup_setup_services;
            this.markup_shipping = this.customer.markup_shipping;
            this.language_id = this.customer.language_id;
            this.$refs['order-lang'].setLanguageID(this.customer.language_id);
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/countries/items',
        loadedKey: 'loadedCountries',
        outputKey: 'countries',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/currencies/items',
        loadedKey: 'loadedCurrencies',
        outputKey: 'currencies',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError',
        params: {
          where: {
            order_id: parseInt(this.$route.params.id, 10)
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/services/all-items',
        loadedKey: 'loadedServices',
        outputKey: 'services',
        errorKey: 'loadError',
        params: {
          where: {
            order_id: parseInt(this.$route.params.id, 10)
          }
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'GET',
        endpoint: '/customer-panel/services/groups/items',
        loadedKey: 'loadedServicesGroups',
        outputKey: 'servicesGroups',
        errorKey: 'loadError',
        params: {
          return: 'get'
        }
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/customer-panel/embroidery/items',
        loadedKey: 'loadedEmbroideryItems',
        outputKey: 'embroideryItems',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/tags/categories/items',
        loadedKey: 'loadedTagCategories',
        outputKey: 'tagCategories',
        errorKey: 'loadError',
        params: {
          with: ['tags']
        }
      });

      FormUtils.loadAdditionalData(this, {
        endpoint: '/customer-panel/processing-time/items',
        loadedKey: 'loadedProcessingTimes',
        outputKey: 'processingTimes',
        errorKey: 'loadError'
      });
    },
    prepareFormData (statusToSet) {
      let boxesCount = this.$refs['request-creator'].cartonsAmount();

      let formData = {
        customer_id: this.customer.id,
        currency_id: this.currency_id,
        address_id: this.$refs['request-address'].address_id,
        shipper_id: this.shipper_id,
        language_id: this.language_id,
        status: statusToSet,
        customer_description: this.customer_description,
        shipper_price: this.shipper_price,
        shipper_price_before_discount: this.shipper_price_before_discount,
        realization_date: this.realization_date,
        processing_time_id: this.processing_time_id,
        products: this.$refs['request-creator'].prepareProductsData(),
        services: this.$refs['request-creator'].prepareServicesData(),
        order_name: this.order_name,
        shipper_cartons_number: parseInt(boxesCount.amount, 10),
        offer_for_each_product: this.offer_for_each_product
      }

      if (this.isEditMode) {
        if (this.addressHasBeenChanged) {
          formData.address_changed = true;
        }
      }

      if (this.isMainUser) {
        formData.contact_id = this.$refs['request-contact'].contact_id;
      }

      if (this.whoOrders === 'my-customer') {
        formData.partner_customer_id = this.$refs['request-customer'].customer_id;
        formData.markup_products = this.markup_products;
        formData.markup_services = this.markup_services;
        formData.markup_setup_services = this.markup_setup_services;
        formData.markup_shipping = this.markup_shipping;
        formData.partner_shipper_price = this.partner_shipper_price;
        formData.partner_contact_id = this.$refs['request-customer-contact'].contact_id;
        formData.partner_include_discounts = !!this.partner_include_discounts;
        formData.partner_discount_products = this.partner_discount_products;
        formData.partner_discount_services = this.partner_discount_services;
        formData.partner_discount_shipping = this.partner_discount_shipping;
        formData.use_customer_pricing = !!this.use_customer_pricing;

        if (this.partner_include_discounts) {
          formData.partner_shipper_price_before_discount = this.partner_shipper_price_before_discount;
        }
      } else {
        formData.markup_products = 0;
        formData.markup_services = 0;
        formData.markup_setup_services = 0;
        formData.markup_shipping = 0;
        formData.use_customer_pricing = false;
      }

      if (!this.isViewMode && this.$refs['customer-request-files'] && this.$refs['customer-request-files'].token) {
        formData.files_token = this.$refs['customer-request-files'].token;
      }

      return formData;
    },
    showContactPersonNewPopup (mode) {
      this.contactPersonPopupMode = mode ? 'partner' : 'partner-customer';
      this.showContactPersonAddPopup = true;
    },
    closeContactPersonAddPopup (contactID) {
      this.showContactPersonAddPopup = false;

      if (contactID) {
        if (this.contactPersonPopupMode === 'partner') {
          this.$bus.$emit('order-select-contact-fetch-data', contactID);
        } else {
          this.$bus.$emit('order-select-customer-contact-fetch-data', contactID);
        }
      }

      this.contactPersonPopupMode = false;
    },
    showAddressNewPopup () {
      this.showAddressAddPopup = true;
    },
    closeAddressAddPopup (addressID) {
      this.showAddressAddPopup = false;

      if (addressID) {
        this.$bus.$emit('order-select-address-fetch-data', addressID);
      }
    },
    dataFetchSuccess (dataKey) {
      if (this.loadedData.indexOf(dataKey) === -1) {
        this.loadedData.push(dataKey);
        this.loadedData.sort();
      }
    },
    setUpOrder () {
      this.customer_id = this.requestData.customer_id;
      this.currency_id = this.requestData.currency_id;
      this.shipper_id = this.requestData.shipper_id;
      this.status = this.requestData.status;
      this.partner_customer_id = this.requestData.partner_customer_id;
      this.customer_description = this.requestData.customer_description;
      this.shipper_price = this.requestData.shipper_price;
      this.shipper_price_before_discount = this.requestData.shipper_price_before_discount;
      this.partner_shipper_price = this.requestData.partner_shipper_price;
      this.partner_shipper_price_before_discount = this.requestData.partner_shipper_price_before_discount;
      this.order_name = this.requestData.order_name;
      this.offer_for_each_product = !!this.requestData.offer_for_each_product;
      this.use_customer_pricing = !!this.requestData.use_customer_pricing;
      this.realization_date = this.requestData.realization_date ? [this.requestData.realization_date.getFullYear(), this.requestData.realization_date.getMonth() + 1, this.requestData.realization_date.getDate()].map(n => n < 10 ? '0' + n : n).join('-') : null;
      this.processing_time_id = this.requestData.processing_time_id;

      if (this.partner_customer_id) {
        this.whoOrders = 'my-customer';
        this.markup_products = this.requestData.markup_products;
        this.markup_services = this.requestData.markup_services;
        this.markup_setup_services = this.requestData.markup_setup_services;
        this.markup_shipping = this.requestData.markup_shipping;
      }

      if (this.whoOrders === 'my-customer') {
        this.partner_include_discounts = !!this.requestData.partner_include_discounts;

        if (this.partner_include_discounts) {
          this.partner_discount_products = this.requestData.partner_discount_products;
          this.partner_discount_services = this.requestData.partner_discount_services;
          this.partner_discount_shipping = this.requestData.partner_discount_shipping;
        } else {
          this.partner_discount_products = 0;
          this.partner_discount_services = 0;
          this.partner_discount_shipping = 0;
        }
      }

      Vue.nextTick(() => {
        if (this.isEditMode) {
          if (this.$refs['request-contact']) {
            this.$refs['request-contact'].setContactID(this.requestData.contact_id);
          }

          this.$refs['request-address'].setAddressID(this.requestData.address_id);
          this.language_id = this.requestData.language_id;
          this.$refs['order-lang'].setLanguageID(this.requestData.language_id);
          this.$refs['request-creator'].setUpProducts(this.requestData.products);
          this.$refs['request-creator'].setUpServices(this.requestData.services);

          if (this.requestData.partner_customer_id) {
            this.$refs['request-customer-contact'].setContactID(this.requestData.partner_contact_id);
          }
        } else {
          this.$refs['request-creator'].setUpProducts(this.requestData.products);
          this.$refs['request-creator'].setUpServices(this.requestData.services);
        }

        Vue.nextTick(() => {
          this.orderSetUpInProgress = false;

          Vue.nextTick(() => {
            this.debouncedShippingPriceCheck();

            if (this.isEditMode && this.isDraftMode) {
              this.$refs['request-creator'].checkPricesInOrder();
            }
          });
        });
      });
    },
    getServiceNameByID (id) {
      let foundedService = this.services.filter(service => service.id === parseInt(id, 10));

      if (foundedService[0]) {
        return [foundedService[0].group_name, foundedService[0].name].filter(item => !!item).join(' - ');
      }

      return '??';
    },
    setCustomerID (newData) {
      this.partner_customer_id = newData.customer_id;
      this.current_partner_discount_products = newData.discounts.products;
      this.current_partner_discount_services = newData.discounts.services;
      this.current_partner_discount_shipping = newData.discounts.shipping;

      if (!this.orderSetUpInProgress) {
        this.addressHasBeenChanged = true;
      }

      if (!this.orderSetUpInProgress && this.partner_include_discounts) {
        this.partner_discount_products = this.current_partner_discount_products;
        this.partner_discount_services = this.current_partner_discount_services;
        this.partner_discount_shipping = this.current_partner_discount_shipping;
      }
    },
    updateCurrentDiscounts (newData) {
      this.current_partner_discount_products = newData.products;
      this.current_partner_discount_services = newData.services;
      this.current_partner_discount_shipping = newData.shipping;
    },
    beforeAddOrder () {
      FormUtils.validate(this.$refs['request-form'], this.tryAddOrder, false, this.showFormErrorsNotice);
    },
    beforeAddDraft () {
      FormUtils.validate(this.$refs['request-form'], this.tryAddDraft, false, this.showFormErrorsNotice);
    },
    showFormErrorsNotice () {
      Vue.swal({
        title: this.$t('order.checkFormTitle'),
        html: this.$t('order.checkFormDesc'),
        type: 'warning',
        confirmButtonText: this.$t('ui.ok'),
        buttonsStyling: true
      });
    },
    tryAddDraft () {
      if (this.isViewMode) {
        return;
      }

      if (!this.orderNameIsFree) {
        return;
      }

      let dataToSend = this.prepareFormData('DRAFT');
      let endpoint = '/customer-panel/orders/store';

      if (this.isEditMode) {
        endpoint = '/customer-panel/orders/update/' + this.$route.params.id;

        if (this.hasPriceMultipleFromChanges) {
          dataToSend.order_prices_updated = true;
        }
      }

      let filesToSend = this.prepareFormFiles();

      FormUtils.submit({
        $http: this.$http,
        endpoint,
        formData: dataToSend,
        successTitle: this.$t('order.editDraftSuccessTitle'),
        successText: this.$t('order.editDraftSaveSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.editDraftErrorText'),
        successAction: () => {
          this.$router.push('/oferty/');
        }
      }, filesToSend);
    },
    tryAddOrder () {
      if (this.isViewMode) {
        return;
      }

      if (!this.orderNameIsFree) {
        return;
      }

      if (!this.$refs['request-creator']) {
        Vue.swal({
          title: this.$t('inquiries.unexpectedErrorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        return;
      }

      if (!this.$refs['request-creator'].productsData.length) {
        Vue.swal({
          title: this.$t('inquiries.caanotCreateOrder'),
          html: this.$t('inquiries.failedToAddProductsToOrder'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true,
          showCancelButton: true,
          cancelButtonText: this.$t('ui.cancel')
        });
        return;
      }

      Vue.swal({
        title: this.$t('order.saveAsOrderNoticeTitle'),
        html: this.$t('order.saveAsOrderNoticeDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('order.yesSave'),
        cancelButtonText: this.$t('ui.cancel'),
        buttonsStyling: true,
      }).then((result) => {
        if (result.value || result.value === '') {
          this.saveAsOrder();
        }
      });
    },
    saveAsOrder () {
      let dataToSend = this.prepareFormData('CUSTOMER_ORDER');

      let endpoint = '/customer-panel/orders/store';

      if (this.isEditMode) {
        endpoint = '/customer-panel/orders/update/' + this.$route.params.id;

        if (this.hasPriceMultipleFromChanges) {
          dataToSend.order_prices_updated = true;
        }
      }

      let filesToSend = this.prepareFormFiles();

      FormUtils.submit({
        $http: this.$http,
        endpoint,
        formData: dataToSend,
        successTitle: this.$t('purchase.editOrderSuccessTitle'),
        successText: this.$t('purchase.yourOrderSaveSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('purchase.addOrderErrorText'),
        successAction: () => {
          this.$router.push('/zamowienia')
        }
      }, filesToSend);
    },
    prepareFormFiles () {
      let files = [];

      for (let i = 0; i < this.$refs['request-creator'].servicesData.length; i++) {
        let serviceData = this.$refs['request-creator'].servicesData[i];

        if (serviceData.fileToUpload) {
          files.push({
            key: 'services[' + i + '][logo]',
            value: serviceData.fileToUpload
          });
        }
      }

      for (let i = 0; i < this.$refs['request-creator'].productsData.length; i++) {
        let productData = this.$refs['request-creator'].productsData[i];

        for (let j = 0; j < productData.services.length; j++) {
          let serviceData = productData.services[j];

          if (serviceData.fileToUpload) {
            files.push({
              key: 'logo_files[' + i + '][' + j + ']',
              value: serviceData.fileToUpload
            });
          }
        }
      }

      return files;
    },
    downloadPDFforMe () {
      Vue.swal({
        title: this.$t('order.pdfDownloadNoticeTitle'),
        html: this.$t('order.pdfDownloadNoticeDesc'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.okUnderstand'),
        cancelButtonText: this.$t('ui.cancel'),
        buttonsStyling: true,
      }).then((result) => {
        if (result.value || result.value === '') {
          this.downloadInProgress = true;

          this.$http.post('/customer-panel/orders/offer-from-hpl/' + this.requestData.id, {}, {
            responseType: 'blob'
          }).then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            let fileName = this.requestData.order_name.replace(/[^a-z\-0-9_]/gmi, '-').replace(/\-{1,}/gmi, '-') + this.$t('order.fileSuffixOfferMe') + '.pdf';
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.downloadInProgress = false;
          }).catch(error => {
            console.log(error);
            Vue.swal({
              title: this.$t('ui.errorOccuredDuringDownload'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
            this.downloadInProgress = false;
          });
        }
      });
    },
    downloadPDF () {
      this.downloadInProgress = true;

      this.$http.post('/customer-panel/orders/offer/' + this.requestData.id, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        let fileName = this.requestData.order_name.replace(/[^a-z\-0-9_]/gmi, '-').replace(/\-{1,}/gmi, '-') + this.$t('order.fileSuffixOfferCustomer') + '.pdf';
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.downloadInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadInProgress = false;
      });
    },
    openContainerSearchPopup (productID, orderProductID, missingAmount) {
      this.selectedContainerProduct = productID;
      this.selectedContainerOrderProduct = orderProductID;
      this.missingProductAmount = missingAmount;
      this.showContainerSearchPopup = true;
    },
    closeContainerSearchPopup () {
      this.showContainerSearchPopup = false;
    },
    debouncedShippingPriceCheck: debounce(function () {
      this.shippingPriceCheck();
    }, 1000),
    shippingPriceCheck () {
      if (this.orderSetUpInProgress || this.isViewMode) {
        return;
      }

      if (!this.$refs['request-creator'] || !this.$refs['request-creator'].totalProductItemsCount) {
        this.shipper_price_before_discount = '0.00';
        this.shipper_price = '0.00';
        this.partner_shipper_price = '0.00';
        return;
      }

      let boxesCount = this.$refs['request-creator'].cartonsAmount();
      let selectedAddress = this.$refs['request-address'].selectedAddress;
      let selectedCountryID = false;

      if (selectedAddress && selectedAddress.country_id) {
        selectedCountryID = selectedAddress.country_id;
      }

      this.boxesCount = boxesCount.amount;
      this.boxesAlert = boxesCount.productsWithoutPieces;
      this.productsCount = this.$refs['request-creator'].totalProductItemsCount;

      if (!selectedAddress) {
        this.shipper_price_before_discount = '0.00';
        this.shipper_price = '0.00';
        this.partner_shipper_price = '0.00';
      }

      if (!(this.shipper_id && selectedCountryID && this.currency_id) || !boxesCount.amount) {
        return;
      }

      this.shippingPriceUpdateInProgress = true;
      this.errorDuringLoadingShippingPrice = false;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/customer-panel/orders/get-shipper-price',
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('order.errors.shipping_price_not_fetched'),
        formData: {
          shipper_id: this.shipper_id,
          conditions: {
            currency_id: this.currency_id,
            country_id: selectedCountryID,
            boxes_count: boxesCount.amount,
            products_count: this.$refs['request-creator'].totalProductItemsCount,
            discount_percent: parseFloat(this.customer.discount_shipping)
          }
        },
        successAction: (response) => {
          this.shippingPriceUpdateInProgress = false;

          if (response.shipper_price && typeof response.shipper_price.price_before_discount !== 'undefined') {
            this.shipper_price_before_discount = CustomDecimals.roundProperly(response.shipper_price.price_before_discount);
            this.shipper_price = CustomDecimals.roundProperly(response.shipper_price.price);
            this.partner_shipper_price_before_discount = CustomDecimals.roundProperly(parseFloat(this.shipper_price) * ((100 + parseFloat(this.markup_shipping)) / 100));

            if (this.whoOrders === 'my-customer') {
              let shipperPrice = this.partner_shipper_price_before_discount;
              this.partner_shipper_price = CustomDecimals.roundProperly(parseFloat(shipperPrice) - ((this.partner_discount_shipping / 100) * parseFloat(shipperPrice)));
            } else {
              this.partner_shipper_price = CustomDecimals.roundProperly(parseFloat(this.shipper_price) * ((100 + parseFloat(this.markup_shipping)) / 100));
            }
          } else {
            this.errorDuringLoadingShippingPrice = true;
            this.shipper_price_before_discount = '0.00';
            this.shipper_price = '0.00';
            this.partner_shipper_price_before_discount = '0.00';
            this.partner_shipper_price = '0.00'
          }
        },
        failAction: () => {
          this.errorDuringLoadingShippingPrice = true;
          this.shippingPriceUpdateInProgress = false;
        }
      });
    },
    updateOfferLanguage (langID) {
      if (this.orderSetUpInProgress) {
        return;
      }

      if (langID) {
        this.$refs['order-lang'].setLanguageID(langID);
      } else {
        this.$refs['order-lang'].setLanguageID(this.customer.language_id);
      }
    },
    debouncedOrderNameCheck: debounce(function () {
      if (this.isViewMode) {
        return;
      }

      this.orderNameCheck();
    }, 500),
    orderNameCheck () {
      if (this.isEditMode && !this.skippedFirstOrderNameCheck && this.order_name === this.requestData.order_name) {
        this.skippedFirstOrderNameCheck = true;
        return;
      }

      if (this.order_name === '') {
        this.orderNameIsFree = false;
        this.orderNameCheckInProgress = false;
        return;
      }

      if (this.lastCheckedOrderName === this.order_name) {
        return;
      }

      this.orderNameCheckInProgress = true;
      let orderID = parseInt(this.$route.params.id, 10);
      let paramsToSent = {
        order_name: this.order_name
      };

      if (orderID) {
        paramsToSent.id = orderID;
      }

      this.lastCheckedOrderName = this.order_name;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/customer-panel/orders/check-order-name',
        formData: paramsToSent,
        skipErrorDisplay: true,
        successAction: (response) => {
          this.orderNameIsFree = !!response.is_free;
          this.orderNameCheckInProgress = false;
          this.orderNameCheckDone = true;
        },
        failAction: () => {
          this.orderNameCheckInProgress = false;
          this.orderNameIsFree = false;
          this.orderNameCheckDone = true;
        }
      });
    },
    displayPriceChanges (priceChanges) {
      this.showPriceChangesPopup = true;
      this.priceChanges = priceChanges;
    },
    changeOrderCurrency (newValue) {
      if (newValue !== this.currency_id) {
        Vue.set(this, 'currency_id', newValue);

        Vue.nextTick(() => {
          this.$bus.$emit('request-creator-global-services-update-price');
          this.$bus.$emit('request-creator-global-product-update-price');
          this.$bus.$emit('order-update-shipment-cost');
        });
      }
    },
    updateLanguage (newValue) {
      this.language_id = newValue;
    },
    openAddCustomerPopup () {
      this.showAddCustomerPopup = true;
    },
    closeAddCustomerPopup () {
      this.showAddCustomerPopup = false;
    },
    setCustomerInSelectField (customerID) {
      this.$refs['request-customer'].prefetchCustomer(customerID);
    }
  },
  beforeDestroy () {
    this.$bus.$off('ui-select-customer-updated', this.setCustomerID);
    this.$bus.$off('ui-select-customer-prefetched-discounts', this.updateCurrentDiscounts);
    this.$bus.$off('request-data-fetch-success', this.dataFetchSuccess);
    this.$bus.$off('customer-add-contact-person-popup-close', this.closeContactPersonAddPopup);
    this.$bus.$off('customer-add-address-popup-close', this.closeAddressAddPopup);
    this.$bus.$off('order-show-add-contact-person-popup', this.showContactPersonNewPopup);
    this.$bus.$off('order-show-add-address-popup', this.showAddressNewPopup);
    this.$bus.$off('container-search-popup-close', this.closeContainerSearchPopup);
    this.$bus.$off('container-search-popup-open', this.openContainerSearchPopup);
    this.$bus.$off('order-check-shipping-price', this.debouncedShippingPriceCheck);
    this.$bus.$off('order-update-shipment-cost', this.debouncedShippingPriceCheck);
    this.$bus.$off('ui-select-customer-language-updated', this.updateOfferLanguage);
    this.$bus.$off('request-creator-show-price-changes-popup', this.displayPriceChanges);
    this.$bus.$off('order-currency-change', this.changeOrderCurrency);
    this.$bus.$off('order-data-language-id-update', this.updateLanguage);
    this.$bus.$off('order-show-new-customer-popup', this.openAddCustomerPopup);
    this.$bus.$off('order-close-new-customer-popup', this.closeAddCustomerPopup);
    this.$bus.$off('order-customer-added-from-popup', this.setCustomerInSelectField);
  }
};
</script>

<style lang="scss" scoped>
.form-control.is-select {
  background-image: none!important;
  padding: 0!important;
}

pre {
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  padding: 20px;
  white-space: pre-line;
}

.info-data {
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 5px;
  font-family: Arial, sans-serif;
  font-size: 14px;
  padding: 20px;
}

.alert {
  /deep/ strong {
    font-weight: bold!important;
  }

  a {
    color: #fff;
    font-size: 125%;
    font-weight: bold;
    text-decoration: underline;
  }
}
</style>
